import {
  Component,
  ElementRef,
  ChangeDetectionStrategy,
  input,
  model,
  viewChild,
  effect,
} from '@angular/core';
import { mockCols, mockData, mockRows, mockValues } from './mock-data';

type sortArg<T> = keyof T | `-${string & keyof T}`;

export interface PivotTableHeader extends PivotTableField {
  total: boolean;
}

export interface PivotTableField {
  field: string;
  label?: string;
}

export interface PivotTableValue extends PivotTableField {
  operator: 'sum' | 'avg' | 'min' | 'max' | 'count';
  alignment?: 'center' | 'left' | 'right';
}

export type PivotTableFormatter = (
  value: string | number,
  context?: { values: any[]; subset: any[] },
) => string;

export interface PivotTableFormatters {
  [key: string]: PivotTableFormatter;
}

@Component({
  selector: 'ag-raw-pivot-table',
  templateUrl: './raw-pivot-table.component.html',
  styleUrls: ['./raw-pivot-table.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RawPivotTableComponent {
  private container: ElementRef;

  readonly pivotEl = viewChild.required<ElementRef>('pivot');

  readonly data = model<any[]>([]);
  readonly cols = model<PivotTableHeader[]>([]);
  readonly rows = model<PivotTableHeader[]>([]);
  readonly values = model<PivotTableValue[]>([]);
  readonly formatters = input<PivotTableFormatters>({});

  debugGrid = false;

  constructor() {
    effect(() => {
      this.data();
      this.cols();
      this.rows();
      this.values();
      this.rePivot();
    });
  }

  useMockData() {
    this.data.set(mockData);
    this.cols.set(mockCols);
    this.rows.set(mockRows);
    this.values.set(mockValues);
    this.pivot(this.data(), this.cols(), this.rows(), this.values());
  }

  // newer recursive approach to generate the aggregated hash
  // much faster and simpler since the data set is only looped over once
  aggregateV2(
    hash,
    item,
    aggIndex,
    aggKeys: PivotTableHeader[],
    valuesToCalc: PivotTableValue[],
  ) {
    const itemKey = aggKeys[aggIndex];
    const hashKey = item[itemKey.field];

    if (!hash[hashKey]) hash[hashKey] = {};

    if (!hash[hashKey]['values']) hash[hashKey]['values'] = {};

    valuesToCalc.forEach((valueKey) => {
      if (!hash[hashKey]['values'][valueKey.field])
        hash[hashKey]['values'][valueKey.field] = 0;

      switch (valueKey.operator) {
        case 'sum':
          hash[hashKey]['values'][valueKey.field] += Number(
            item[valueKey.field],
          );
          break;

        case 'avg': {
          const currNum = Number(item[valueKey.field]);
          if (!hash[hashKey]['values'][valueKey.field])
            hash[hashKey]['values'][valueKey.field] = currNum;
          else if (!isNaN(currNum) && currNum !== 0)
            hash[hashKey]['values'][valueKey.field] =
              (hash[hashKey]['values'][valueKey.field] +
                Number(item[valueKey.field])) /
              2;
          break;
        }

        case 'count':
          hash[hashKey]['values'][valueKey.field] += 1;
          break;

        case 'min': {
          let currMin = hash[hashKey]['values'][valueKey.field];
          let currVal = item[valueKey.field];
          currVal = isNaN(currVal) ? currVal : Number(currVal);
          if (!currMin) currMin = currVal;
          else if (currVal < currMin) {
            currMin = currVal;
          }

          hash[hashKey]['values'][valueKey.field] = currMin;
          break;
        }
        case 'max': {
          let currMax = hash[hashKey]['values'][valueKey.field];
          let currVal = item[valueKey.field];
          currVal = isNaN(currVal) ? currVal : Number(currVal);
          if (!currMax) currMax = currVal;
          else if (currVal > currMax) {
            currMax = currVal;
          }

          hash[hashKey]['values'][valueKey.field] = currMax;
          break;
        }
      }
    });

    // aggregate next grouping
    if (aggIndex < aggKeys.length - 1) {
      if (!hash[hashKey]['children']) hash[hashKey]['children'] = {};

      this.aggregateV2(
        hash[hashKey]['children'],
        item,
        aggIndex + 1,
        aggKeys,
        valuesToCalc,
      );
    }
  }

  // only the pivot-table "renderer" uses this old approach
  // for any new feature that needs to work with the grouped data, use aggregateV2
  // this one is a lot slower since it repeatedly loops over the entire data set
  generateAggregatedHash(
    data,
    rows: PivotTableHeader[],
    cols: PivotTableHeader[],
    values: PivotTableValue[],
  ) {
    const hash = {};
    for (let index = 0; index < data.length; index++) {
      const element = data[index];
      this.generateRegularCells(data, rows, cols, values, hash, element);
      this.generateTotalCells(data, rows, cols, values, hash, element);
      this.generateTotalCells(data, cols, rows, values, hash, element);
    }

    return hash;
  }

  generateTotalCells(
    data,
    headers: PivotTableHeader[],
    oppositeHeaders: PivotTableHeader[],
    values,
    hash,
    element,
  ) {
    for (let i = 0; i < headers.length; i++) {
      const propertiesToMatch = {};
      const currentHeader = headers[i];

      if (currentHeader.total) {
        if (i > 0) {
          const previousHeader = headers[i - 1].field;

          propertiesToMatch[previousHeader] = element[previousHeader];
        }

        for (let j = 0; j < oppositeHeaders.length; j++) {
          if (oppositeHeaders[j].total) {
            const totalTotalObj = { ...propertiesToMatch };
            totalTotalObj['key2'] = 'total';
            totalTotalObj['key3'] = 'total';

            // add total of total row/col
            this.calculateAndAddToHash(
              data,
              values,
              hash,
              propertiesToMatch,
              this.calculateHashKey(totalTotalObj),
            );
          }

          const groupingField = oppositeHeaders[j].field;
          propertiesToMatch[groupingField] = element[groupingField];
        }
        // add row/col total
        const newHashKey = this.calculateHashKey({
          ...propertiesToMatch,
          totalKey: 'total',
        });
        this.calculateAndAddToHash(
          data,
          values,
          hash,
          propertiesToMatch,
          newHashKey,
        );
      }
    }
  }

  generateRegularCells(data, rows, cols, values, hash, element) {
    const propertiesToMatch = {};
    for (let i = 0; i < rows.length; i++) {
      const groupingField = rows[i].field;
      propertiesToMatch[groupingField] = element[groupingField];
    }
    for (let i = 0; i < cols.length; i++) {
      const groupingField = cols[i].field;
      propertiesToMatch[groupingField] = element[groupingField];
    }

    this.calculateAndAddToHash(data, values, hash, propertiesToMatch);
  }

  calculateAndAddToHash(
    data,
    values,
    hash,
    propertiesToMatch,
    overriddenHashKey = null,
  ) {
    const hashKey =
      overriddenHashKey || this.calculateHashKey(propertiesToMatch);
    if (!hash[hashKey]) {
      const hashValues = [];

      values.forEach((_v) => hashValues.push(null));

      // e is each actual item in the data set
      const matchingRows = data.filter((e) => {
        let itemHasAllFields = true;
        for (const key in propertiesToMatch) {
          if (e[key] != propertiesToMatch[key]) {
            itemHasAllFields = false;
            continue;
          }
        }

        if (!itemHasAllFields) return false;

        for (let ind = 0; ind < values.length; ind++) {
          const valueToCalc = values[ind];
          // the current aggregated value
          let val = hashValues[ind];

          let curNum: number;

          switch (valueToCalc.operator) {
            case 'sum':
              curNum = Number(e[valueToCalc.field]);
              if (!val) val = curNum;
              else val += curNum;
              break;
            case 'avg':
              curNum = Number(e[valueToCalc.field]);
              if (!val) val = curNum;
              else if (!isNaN(curNum) && curNum !== 0) val = (curNum + val) / 2;

              break;
            case 'min':
              if (!val) val = e[valueToCalc.field];
              else if (e[valueToCalc.field] < val) val = e[valueToCalc.field];
              break;
            case 'max':
              if (!val) val = e[valueToCalc.field];
              else if (e[valueToCalc.field] > val) val = e[valueToCalc.field];
              break;
            case 'count':
              if (!val) val = 1;
              else val += 1;
              break;
            default:
              break;
          }

          hashValues[ind] = val;
        }

        return true;
      });

      hash[hashKey] = { values: hashValues, subset: matchingRows };
    }
  }

  calculateHashKey(object) {
    const keyValues = Object.values(object).sort();

    return keyValues.join('_');
  }

  rePivot(): void {
    this.pivot(this.data(), this.cols(), this.rows(), this.values());
  }

  pivot(
    data,
    cols: PivotTableHeader[],
    rows: PivotTableHeader[],
    values: PivotTableValue[],
  ) {
    if (!this.container) return;

    this.container.nativeElement.innerHTML = '';

    const uniqueRows = this.uniquePropertyValues(data, rows);
    const uniqueCols = this.uniquePropertyValues(data, cols);

    const aggregatedHash = this.generateAggregatedHash(
      data,
      rows,
      cols,
      values,
    );

    uniqueRows.sort(this.byPropertiesOf<any>(rows.map((r) => r.field)));
    uniqueCols.sort(this.byPropertiesOf<any>(cols.map((r) => r.field)));

    // console.log('hash', aggregatedHash);
    // console.log('cols', cols);
    // console.log('rows', rows);
    // console.log('values', values);
    // console.log('uniqueCols', uniqueCols);

    const totalCols = rows.length + uniqueCols.length * values.length;
    const totalRows = cols.length + uniqueRows.length;

    const table = document.createElement('table');
    table.className = 'min-w-full';

    for (let row = 0; row <= totalRows; row++) {
      const tr = table.insertRow(-1);
      //   tr.className =
      //     'text-sm tracking-wide text-left whitespace-nowrap text-slate-600 odd:bg-slate-50 even:bg-slate-100 hover:bg-slate-200 border-2';
      for (let col = 0; col <= totalCols; col++) {
        // deal with case when no columns are selected
        if (row == 0 && cols.length == 0 && col > rows.length) {
          const offsetCol = col - rows.length - 1;

          const indexOfValueToCalc = offsetCol % values.length;

          const cell = tr.insertCell(-1);
          cell.className = 'p-2 border-2 bg-slate-100 hover:bg-slate-200';
          cell.innerHTML =
            values[indexOfValueToCalc].label ||
            `${values[indexOfValueToCalc].operator} of ${values[indexOfValueToCalc].field}`;

          // cell.innerHTML = 'total';
        }
        // deal with case when no rows are selected
        else if (col == 0 && rows.length == 0 && row > cols.length) {
          const cell = tr.insertCell(-1);
          cell.className = 'p-2 border-2 bg-slate-100 hover:bg-slate-200';

          if (values.length > 1 || values.length == 0) {
            cell.innerHTML = 'Totals';
          } else {
            const offsetRow = row - cols.length - 1;

            const indexOfValueToCalc = offsetRow % values.length;
            cell.innerHTML =
              values[indexOfValueToCalc].label ||
              `${values[indexOfValueToCalc].operator} of ${values[indexOfValueToCalc].field}`;
          }
        }
        //fill in top left corner
        else if (row < cols.length && col < rows.length) {
          if ((row == 0 && col == 0) || this.debugGrid) {
            const cell = tr.insertCell(-1);
            cell.className = 'p-2 border-2 bg-slate-100 hover:bg-slate-200';
            cell.innerHTML = '&nbsp;';

            if (values.length == 1 && row == 0 && col == 0) {
              cell.innerHTML =
                values[0].label ||
                `${values[0].operator} of ${values[0].field}`;
            }

            if (!this.debugGrid) {
              cell.colSpan = rows.length;
              cell.rowSpan = cols.length;
            }
          }
        }
        // fill in row/cols between
        else if (
          (row == cols.length && col >= rows.length) ||
          (col == rows.length && row >= cols.length)
        ) {
          if (row == cols.length && col > rows.length && values.length > 1) {
            const offsetCol = col - rows.length - 1;

            const indexOfValueToCalc = offsetCol % values.length;

            const cell = tr.insertCell(-1);
            cell.className = 'p-2 border-2 bg-slate-100 hover:bg-slate-200';
            cell.innerHTML =
              values[indexOfValueToCalc].label ||
              `${values[indexOfValueToCalc].operator} of ${values[indexOfValueToCalc].field}`;
          } else if (
            (row == cols.length && col == rows.length) ||
            this.debugGrid
          ) {
            const cell = tr.insertCell(-1);
            cell.className = 'p-2 border-2 bg-slate-100 hover:bg-slate-200';
            cell.innerHTML = '&nbsp;';
          }
        }
        // put in table column headers
        else if (row < cols.length && col >= rows.length) {
          this.fillHeaders(
            tr,
            row,
            col,
            cols,
            rows,
            uniqueCols,
            values,
            'rowSpan',
          );
        }
        // put in table row headers
        else if (col < rows.length && row >= cols.length) {
          this.fillHeaders(
            tr,
            col,
            row,
            rows,
            cols,
            uniqueRows,
            null,
            'colSpan',
          );
        }
        // fills in the actual table cells/values
        else if (row > cols.length && col > rows.length) {
          const cell = tr.insertCell(-1);
          cell.className = 'p-2 border-2 bg-slate-50 hover:bg-slate-200';
          cell.innerHTML = '&nbsp;';
          // where this cell sits, ignoring headers
          const offsetCol = col - rows.length - 1;
          const offsetRow = row - cols.length - 1;

          const indexOfValueToCalc = offsetCol % values.length;

          const fields = Object.assign(
            {},
            uniqueCols[Math.floor(offsetCol / values.length)],
            uniqueRows[offsetRow],
          );

          const cellKey = this.calculateHashKey(fields);

          const formatter = this.formatters()[values[indexOfValueToCalc].field];

          let cellValue = aggregatedHash[cellKey]?.values[indexOfValueToCalc];

          if (formatter && values[indexOfValueToCalc].operator != 'count')
            cellValue = formatter(cellValue, aggregatedHash[cellKey]);

          cell.innerHTML = cellValue || '';

          const alignment = values[indexOfValueToCalc].alignment;

          if (alignment) cell.setAttribute('align', alignment);

          if (cellKey.includes('total')) {
            cell.className += ' font-semibold';
          }
        }
      }
    }

    this.container.nativeElement.appendChild(table);
  }

  fillHeaders(
    tr: HTMLTableRowElement,
    row: number,
    col: number,
    headers: PivotTableHeader[],
    otherHeaders: PivotTableHeader[],
    uniqueItems,
    values,
    cellFill,
  ) {
    const currProperty = headers[row]?.field;

    // set header for each selected col
    if (col - otherHeaders.length == 0 && row < headers.length) {
      const cell = tr.insertCell(-1);
      cell.className = 'p-2 border-2 bg-slate-50 hover:bg-slate-200';
      cell.innerHTML = '&nbsp;';
      cell.className += ' bg-slate-100';
      cell.innerHTML = headers[row]?.label || headers[row]?.field;
    }
    // insert header for each item in selected col
    else if (row < headers.length) {
      const headerIndex = Math.floor(
        (col - otherHeaders.length - 1) / (values?.length || 1),
      );
      const currHeader = uniqueItems[headerIndex];

      let currHeaderVal: string = currHeader[currProperty];

      let shouldInsertCell = true;
      let spanLength = 1;

      // there's a header after this one
      if (headerIndex < uniqueItems.length) {
        for (let i = headerIndex + 1; i < uniqueItems.length; i++) {
          const nextHeader = uniqueItems[i];
          const nextHeaderVal = nextHeader[currProperty];

          if (row > 0) {
            const prevProperty = headers[row - 1]?.field;
            const currHeaderPrevVal = currHeader[prevProperty];
            const nextHeaderPrevVal = nextHeader[prevProperty];
            if (currHeaderPrevVal !== nextHeaderPrevVal) {
              break;
            }
          }

          // first pivot row/col needs to be the same
          if (currHeader[headers[0]?.field] !== nextHeader[headers[0]?.field]) {
            break;
          }

          if (nextHeaderVal == currHeaderVal) {
            spanLength++;
          } else break;
        }
      }

      // there's a header before this one
      if (headerIndex > 0) {
        const prevHeader = uniqueItems[headerIndex - 1];
        const prevHeaderVal = prevHeader[currProperty];

        let isStillSameParent = true;
        if (row > 0) {
          const prevProperty = headers[row - 1]?.field;
          const currHeaderPrevVal = currHeader[prevProperty];
          const prevHeaderPrevVal = prevHeader[prevProperty];
          if (currHeaderPrevVal != prevHeaderPrevVal) {
            isStillSameParent = false;
          }
        }

        // first pivot row/col needs to be the same
        if (currHeader[headers[0]?.field] != prevHeader[headers[0]?.field]) {
          isStillSameParent = false;
        }

        if (prevHeaderVal == currHeaderVal && isStillSameParent) {
          shouldInsertCell = false;
        }
      }

      if (shouldInsertCell || this.debugGrid) {
        const cell = tr.insertCell(-1);
        cell.className = 'p-2 border-2 bg-slate-50 hover:bg-slate-200';
        cell.innerHTML = '&nbsp;';
        cell.className += ' bg-slate-100';

        if (spanLength > 1 && !this.debugGrid) {
          cell[cellFill == 'colSpan' ? 'rowSpan' : 'colSpan'] = spanLength;
        }

        if (
          row == headers.length - 1 &&
          col - otherHeaders.length > 0 &&
          !this.debugGrid &&
          (values == null || values.length == 1)
        ) {
          cell[cellFill] = 2;
        }

        currHeaderVal = currHeaderVal ? String(currHeaderVal) : '';

        if (currHeaderVal?.includes('total')) {
          currHeaderVal = currHeaderVal.replace('total', 'Total');
          cell.className += ' font-semibold';
        }

        cell.innerHTML = currHeaderVal;
      }
    }
  }

  uniquePropertyValues(objects, properties: PivotTableHeader[]) {
    const result = {};

    objects.forEach((o) => {
      const propertiesToMatch = {};
      for (let i = 0; i < properties.length; i++) {
        const groupingField = properties[i].field;
        propertiesToMatch[groupingField] = o[groupingField];

        if (properties[i].total) {
          const totalForField =
            i == 0 ? groupingField : properties[i - 1].field;
          const totalObj = {};
          totalObj[totalForField] = i == 0 ? 'total' : o[totalForField];
          totalObj[groupingField] = 'total';
          const hashKey = this.calculateHashKey(totalObj);
          if (!result[hashKey]) result[hashKey] = totalObj;
        }
      }
      const hashKey = this.calculateHashKey(propertiesToMatch);

      if (!result[hashKey]) result[hashKey] = propertiesToMatch;
    });

    // const propertiesToMatch = {};
    // const element = properties[0];
    // if (element.total) {
    //   propertiesToMatch[element.field] = 'total';
    //   const hashKey = this.calculateHashKey(propertiesToMatch);
    //   result[hashKey] = propertiesToMatch;
    // }

    return Object.values(result);
  }

  byPropertiesOf<T extends object>(sortBy: Array<sortArg<T>>) {
    function compareByProperty(arg: sortArg<T>) {
      let key: keyof T;
      let sortOrder = 1;
      if (typeof arg === 'string' && arg.startsWith('-')) {
        sortOrder = -1;
        // Typescript is not yet smart enough to infer that substring is keyof T
        key = arg.substr(1) as keyof T;
      } else {
        // Likewise it is not yet smart enough to infer that arg is not keyof T
        key = arg as keyof T;
      }
      return function (a: T, b: T) {
        if (typeof a[key] === 'string' && (a[key] as any).includes('total')) {
          return 1 * sortOrder;
        }

        if (typeof b[key] === 'string' && (b[key] as any).includes('total')) {
          return -1 * sortOrder;
        }
        const result = a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0;

        return result * sortOrder;
      };
    }

    return function (obj1: T, obj2: T) {
      let i = 0;
      let result = 0;
      const numberOfProperties = sortBy?.length;
      while (result === 0 && i < numberOfProperties) {
        result = compareByProperty(sortBy[i])(obj1, obj2);
        i++;
      }

      return result;
    };
  }
}
